import { motion } from "framer-motion";
import { Flame, Zap } from "lucide-react";
import AnimatedCounter from "./AnimatedCounter";
import type { StreakWeek } from "@/hooks/useWrappedStats";

interface StreakSlideProps {
  longestStreak: number;
  streakDetails: StreakWeek[];
}

const StreakSlide = ({ longestStreak, streakDetails }: StreakSlideProps) => {
  if (longestStreak === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center px-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          className="mb-6"
        >
          <Zap className="w-16 h-16 text-secondary" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-2xl font-bold text-background mb-4"
        >
          Quality Over Quantity
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-background/70"
        >
          You enjoyed art at your own pace in 2024
        </motion.p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center text-center px-6 w-full max-w-md">
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="mb-2 relative"
      >
        <Flame className="w-16 h-16 text-orange-500" />
        <motion.div
          className="absolute inset-0"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.8, 1, 0.8],
          }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Flame className="w-16 h-16 text-orange-400" />
        </motion.div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-lg text-background/80 mb-1"
      >
        Your longest streak was
      </motion.p>

      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, type: "spring" }}
        className="flex items-baseline gap-2"
      >
        <AnimatedCounter
          value={longestStreak}
          className="text-6xl font-bold text-orange-500"
        />
        <span className="text-xl text-background">
          {longestStreak === 1 ? "week" : "weeks"}
        </span>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-background/70 mt-1 mb-4"
      >
        in a row with art experiences! 🔥
      </motion.p>

      {/* Streak timeline */}
      {streakDetails.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="w-full space-y-1.5"
        >
          {streakDetails.slice(0, 4).map((week, idx) => (
            <motion.div
              key={week.week_start}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 + idx * 0.1 }}
              className="flex items-center gap-2 bg-background/10 rounded-lg p-2"
            >
              <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                <Flame className="w-3.5 h-3.5 text-primary" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-xs font-medium text-background truncate">
                  {week.events[0]}
                </p>
                <p className="text-xs text-background/60">
                  Week of {new Date(week.week_start).toLocaleDateString("en-GB", { month: "short", day: "numeric" })}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default StreakSlide;
